import { useTranslation } from 'react-i18next';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from './msalConfig';

/**
 * Shown instead of the application when the user is not signed in, or when the API has refused
 * their token. Always offers a way forward — an endless spinner or a bare error is not a
 * recoverable state for someone who simply needs to sign in with a different account.
 */
export function SignInRequired({ reason }: { reason: 'signedOut' | 'notAllowed' }) {
  const { t } = useTranslation();
  const { instance } = useMsal();

  const signIn = () => {
    // prompt=select_account matters for the notAllowed case: without it the browser silently
    // re-presents the same rejected account and the user cannot escape.
    void instance.loginRedirect({ ...loginRequest, prompt: 'select_account' });
  };

  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}
      data-testid={reason === 'notAllowed' ? 'access-denied' : 'sign-in-required'}
    >
      <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, borderRadius: '10px', maxWidth: 460, width: '100%' }}>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Box component="img" src="/brand/aiqbee-square.png" alt="" sx={{ width: 56, height: 56 }} />
          <Typography variant="h2">
            {reason === 'notAllowed' ? t('auth.notAllowedTitle') : t('auth.signInTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {reason === 'notAllowed' ? t('auth.notAllowedBody') : t('auth.signInBody')}
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1, width: '100%' }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<LoginIcon />}
              onClick={signIn}
              data-testid="sign-in-button"
            >
              {reason === 'notAllowed' ? t('auth.signInDifferent') : t('auth.signIn')}
            </Button>
            {reason === 'notAllowed' && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<LogoutIcon />}
                onClick={() => void instance.logoutRedirect()}
                data-testid="sign-out-button"
              >
                {t('auth.signOut')}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
